import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Shell } from "./Shell";
import { FadeUp } from "./Sections";
import { RegionMap } from "./RegionMap";
import { EmissionsChart } from "./EmissionsChart";

const HERO = "https://harcresearch.org/wp-content/uploads/2026/03/shutterstock_2618287935.jpg";
const GALLERY = "https://harcresearch.org/wp-content/uploads/2026/04/iStock-910070326-scaled.jpg";

const BLOCKS = [
  {
    num: "01",
    color: "#004778",
    title: "The Challenge",
    body: [
      "Led by the City of San Antonio's Department of Resilience and Sustainability (DRS) and Alamo Area Council of Governments (AACOG), the Alamo Area Climate Plan is the first-of-its-kind regional roadmap to reduce air pollution across their 13-county region.",
      "The plan outlines a comprehensive strategy to cut greenhouse gas emissions, strengthen community resilience, and support sustainable economic growth. Overall, the Plan's goal is to reduce 2021 level emissions by 50% by 2050, with an 80% reduction as an aspirational goal.",
    ],
  },
  {
    num: "02",
    color: "#83BDE4",
    title: "HARC's Approach",
    body: [
      "HARC was brought on board to provide technical assistance, stakeholder engagement, and communications support to the City of San Antonio's DRS and AACOG.",
      "This included assessing, strengthening, finalizing, and promoting the draft Alamo Area Climate Plan, helping fill analytical and engagement gaps, ensuring both technical accuracy and stakeholder alignment.",
    ],
  },
  {
    num: "03",
    color: "#7EBC37",
    title: "The Solution",
    placeholder: "[HARC CONTENT REQUIRED: final plan deliverables and adoption details, verified by the project team]",
  },
  {
    num: "04",
    color: "#FFA100",
    title: "The Impact",
    placeholder: "[HARC CONTENT REQUIRED: verified outcomes and metrics; no statistics are shown until confirmed]",
  },
];

const RELATED = [
  {
    type: "Report",
    title: "HARC's Study on Floating Solar Feasibility Published in Sustainability Journal",
    href: "https://harcresearch.org/news/harcs-study-on-floating-solar-feasibility-published-in-sustainability-journal/",
  },
  {
    type: "Report",
    title: "HARC Releases New Report on Building a Critical Energy and Water Workforce in Harris County",
    href: "https://harcresearch.org/news/harc-releases-new-report-on-building-a-critical-energy-and-water-workforce-in-harris-county/",
  },
];

const MetaItem = ({ label, children }) => (
  <div>
    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#5C6B72]">{label}</p>
    <p className="mt-1.5 text-sm font-semibold text-[#022A42]">{children}</p>
  </div>
);

export default function ImpactStory() {
  return (
    <Shell>
      <article data-testid="impact-story-page">
        <div className="mx-auto max-w-7xl px-6 pt-14">
          <Link
            to="/#stories"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[#004778] transition-colors hover:text-[#FFA100]"
            data-testid="story-back-link"
          >
            <ArrowLeft size={14} aria-hidden="true" /> All Impact Stories
          </Link>
          <FadeUp>
            <h1 className="harc-heading mt-6 max-w-3xl text-4xl font-medium leading-[1.1] tracking-tight text-[#004778] md:text-6xl">
              Strengthening the Alamo Area's Climate Plan
            </h1>
            <div
              className="mt-8 grid gap-6 border-y border-[#004778]/20 py-6 sm:grid-cols-3"
              data-testid="story-meta"
            >
              <MetaItem label="Partner Sector">Government</MetaItem>
              <MetaItem label="Topics">Climate & Resilience · Air Quality</MetaItem>
              <MetaItem label="Partners">
                City of San Antonio DRS · Alamo Area Council of Governments
              </MetaItem>
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mx-auto mt-10 max-w-7xl px-6">
          <img
            src={HERO}
            alt="Alamo Area region project imagery"
            className="aspect-[21/9] w-full border border-[#004778]/20 object-cover"
            data-testid="story-hero-image"
          />
        </FadeUp>

        <div className="mx-auto max-w-3xl px-6 py-20">
          {BLOCKS.map((b, i) => (
            <FadeUp key={b.num} delay={0.05} className="border-t border-[#004778]/20 py-12">
              <div className="flex items-baseline gap-5">
                <span className="harc-heading text-sm font-bold" style={{ color: b.color }} aria-hidden="true">
                  {b.num}
                </span>
                <h2 className="harc-heading text-3xl font-medium text-[#004778]">{b.title}</h2>
              </div>
              <div className="mt-6 space-y-4 pl-0 md:pl-12">
                {b.body ? (
                  b.body.map((p, j) => (
                    <p key={j} className="text-base leading-relaxed text-[#5C6B72]">
                      {p}
                    </p>
                  ))
                ) : (
                  <p
                    className="border-l-4 border-[#FFA100] bg-[#004778]/5 p-5 text-sm italic text-[#5C6B72]"
                    data-testid={`story-placeholder-${b.num}`}
                  >
                    {b.placeholder}
                  </p>
                )}
              </div>
              {i === 0 && <RegionMap />}
              {i === 2 && <EmissionsChart />}
              {i === 1 && (
                <div className="mt-10 grid gap-4 md:grid-cols-2">
                  <img
                    src={GALLERY}
                    alt="Field research imagery from the project"
                    className="aspect-[4/3] w-full border border-[#004778]/20 object-cover"
                    loading="lazy"
                  />
                  <div className="flex aspect-[4/3] items-center justify-center border border-dashed border-[#004778]/40 p-6 text-center text-xs italic text-[#5C6B72]">
                    [HARC CONTENT REQUIRED: additional project photos]
                  </div>
                </div>
              )}
            </FadeUp>
          ))}

          <FadeUp className="border-t border-[#004778]/20 py-12">
            <h2 className="harc-heading text-2xl font-medium text-[#004778]">Related Insights</h2>
            <div className="mt-6">
              {RELATED.map((r) => (
                <a
                  key={r.href}
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="insight-row"
                  data-testid={`story-related-${r.type.toLowerCase()}`}
                >
                  <span className="w-fit rounded-full border border-[#004778] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#004778]">
                    {r.type}
                  </span>
                  <span className="harc-heading text-lg leading-snug text-[#022A42]">{r.title}</span>
                  <span />
                  <ArrowRight size={18} className="row-arrow text-[#FFA100]" aria-hidden="true" />
                </a>
              ))}
            </div>
            <p className="mt-6 text-xs italic text-[#5C6B72]">
              Related people: [HARC CONTENT REQUIRED: project team members from the People model]
            </p>
          </FadeUp>
        </div>

        <section className="bg-[#004778]" data-testid="story-cta-banner">
          <div className="brand-stripe" aria-hidden="true" />
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6 py-20">
            <h2 className="harc-heading max-w-xl text-3xl font-light leading-tight text-white md:text-4xl">
              Have a Complex Challenge? Let's Solve It Together.
            </h2>
            <Link
              to="/start-a-conversation"
              data-testid="story-cta"
              className="inline-flex items-center gap-3 rounded-full bg-[#FFA100] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#022A42] transition-colors hover:bg-[#D98A00]"
            >
              Talk to HARC <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </section>
      </article>
    </Shell>
  );
}
